import api from './apiService';

/**
 * Service for page-related operations
 */
const pageService = {
  /**
   * Get all pages for the current user
   * @param {Object} options - Query options
   * @param {number} [options.limit=50] - Max number of results
   * @param {number} [options.offset=0] - Result offset
   * @param {number} [options.website_id] - Optional website ID to filter by
   * @returns {Promise<Object>} Pages with pagination info
   */
  getAllPages: async ({ limit = 50, offset = 0, website_id = null } = {}) => {
    try {
      let params = { limit, offset };
      if (website_id) {
        params.website_id = website_id;
      }
      
      const response = await api.get('/pages', { params });
      return response.data;
    } catch (error) {
      console.error('Error getting pages:', error);
      throw error;
    }
  },

  /**
   * Get page by ID
   * @param {number} id - Page ID
   * @returns {Promise<Object>} Page
   */
  getPageById: async (id) => {
    try {
      const response = await api.get(`/pages/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting page with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get page by URL
   * @param {string} url - Page URL
   * @returns {Promise<Object>} Page
   */
  getPageByUrl: async (url) => {
    try {
      const response = await api.get('/pages/byUrl', {
        params: { url }
      });
      return response.data;
    } catch (error) {
      console.error(`Error getting page with URL ${url}:`, error);
      throw error;
    }
  },

  /**
   * Create a new page
   * @param {Object} pageData - Page data to create
   * @returns {Promise<Object>} Created page
   */
  createPage: async (pageData) => {
    try {
      const response = await api.post('/pages', pageData);
      return response.data;
    } catch (error) {
      console.error('Error creating page:', error);
      throw error;
    }
  },

  /**
   * Update existing page
   * @param {number} id - Page ID
   * @param {Object} pageData - Page data to update
   * @returns {Promise<Object>} Updated page
   */
  updatePage: async (id, pageData) => {
    try {
      const response = await api.put(`/pages/${id}`, pageData);
      return response.data;
    } catch (error) {
      console.error(`Error updating page with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a page
   * @param {number} id - Page ID
   * @returns {Promise<Object>} Response
   */
  deletePage: async (id) => {
    try {
      const response = await api.delete(`/pages/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting page with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get all pages for a specific website
   * @param {number} websiteId - Website ID
   * @param {Object} options - Query options
   * @param {number} [options.limit=50] - Max number of results
   * @param {number} [options.offset=0] - Result offset
   * @returns {Promise<Object>} Pages with pagination info
   */
  getWebsitePages: async (websiteId, { limit = 50, offset = 0 } = {}) => {
    try {
      const response = await api.get(`/websites/${websiteId}/pages`, {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      console.error(`Error getting pages for website ${websiteId}:`, error);
      throw error;
    }
  },

  /**
   * Get PageSpeed data for a specific page
   * @param {number} pageId - Page ID
   * @returns {Promise<Object>} PageSpeed data
   */
  getPageSpeedData: async (pageId) => {
    try {
      const response = await api.get(`/user-page-speeds/page/${pageId}`);
      return response.data;
    } catch (error) {
      console.error(`Error getting PageSpeed data for page ${pageId}:`, error);
      throw error;
    }
  },

  /**
   * Get all PageSpeed data for all pages of a website in a single call
   * @param {number} websiteId - Website ID
   * @returns {Promise<Object>} PageSpeed data for all website pages
   */
  getAllPageSpeedsForWebsite: async (websiteId) => {
    try {
      const response = await api.get(`/user-page-speeds/website/${websiteId}/all-pages`);
      return response.data;
    } catch (error) {
      console.error(`Error getting all PageSpeed data for website ${websiteId}:`, error);
      throw error;
    }
  },

  /**
   * Determine if a page has been analyzed based on PageSpeed data
   * @param {number} pageId - Page ID
   * @returns {Promise<Object>} Analysis status with hasDesktopScore, hasMobileScore, and hasBeenAnalyzed flags
   */
  getAnalysisStatus: async (pageId) => {
    try {
      const response = await api.get(`/user-page-speeds/${pageId}`);
      const pageSpeedData = response.data;
      
      const hasDesktopScore = 
        pageSpeedData && 
        pageSpeedData.performance_score !== null && 
        pageSpeedData.performance_score !== undefined;
      
      const hasMobileScore = 
        pageSpeedData && 
        pageSpeedData.mobile_performance_score !== null && 
        pageSpeedData.mobile_performance_score !== undefined;
      
      const hasBeenAnalyzed = 
        hasDesktopScore || 
        hasMobileScore || 
        (pageSpeedData && pageSpeedData.updated_at);
      
      return {
        hasDesktopScore,
        hasMobileScore,
        hasBeenAnalyzed,
        pageSpeedData
      };
    } catch (error) {
      console.error(`Error determining analysis status for page ${pageId}:`, error);
      return {
        hasDesktopScore: false,
        hasMobileScore: false,
        hasBeenAnalyzed: false,
        pageSpeedData: null
      };
    }
  }
};

export default pageService;